import { defineStore } from 'pinia'
import type { PokemonType } from '~/data/types'
import { usePlayerStore } from '~/stores/usePlayerStore'
import { runCombatDamageTick } from '~/utils/combatDamageTickRegistry'

export interface Enemy {
  nameFr: string
  nameEn: string
  slug: string
  types: PokemonType[]  // Support pour doubles types
  spriteUrl: string
  maxHp: number
  currentHp: number
  level: number
  goldReward: number
  xpReward: number
  isBoss: boolean
  bossTimerSeconds: number | null
  isShiny?: boolean
}

interface CombatState {
  enemy: Enemy | null
  /** Incrémenté à chaque setEnemy — force le rechargement du sprite (même slug). */
  enemySpriteNonce: number
  teamDps: number
  isFighting: boolean
  bossTimeRemaining: number | null
  /** Smooth lerped HP percent (0-100) driven by RAF — use this for rendering */
  displayedHpPercent: number
  autoAttackHandle: ReturnType<typeof setTimeout> | null
  bossTimerHandle: ReturnType<typeof setTimeout> | null
  overrideAutoAttack: (() => void) | null
  /** True for 150ms after each attack tick — drives the sprite hit-flash animation */
  isHit: boolean
  /** True during the 550ms death animation before the next encounter spawns */
  isDying: boolean
  /** True for 300ms when a single hit deals > 15 % of enemy maxHp — drives screen flash */
  bigHitFlash: boolean
  /** Onglet en arrière-plan : combat client en pause (offline serveur). */
  pausedByVisibility: boolean
}

export const useCombatStore = defineStore('combat', {
  state: (): CombatState => ({
    enemy: null,
    enemySpriteNonce: 0,
    teamDps: 0,
    isFighting: false,
    bossTimeRemaining: null,
    displayedHpPercent: 100,
    autoAttackHandle: null,
    bossTimerHandle: null,
    overrideAutoAttack: null,
    isHit: false,
    isDying: false,
    bigHitFlash: false,
    pausedByVisibility: false,
  }),

  getters: {
    enemyHpPercent: (state): number => {
      if (!state.enemy) return 0
      return Math.max(0, (state.enemy.currentHp / state.enemy.maxHp) * 100)
    },
    isEnemyDead: (state): boolean => {
      return state.enemy !== null && state.enemy.currentHp <= 0
    },
    isBossFight: (state): boolean => {
      return state.enemy?.isBoss ?? false
    },
    bossTimedOut: (state): boolean => {
      return state.bossTimeRemaining !== null && state.bossTimeRemaining <= 0
    },
  },

  actions: {
    setEnemy(enemy: Enemy) {
      this.clearTimers()
      const prev = this.enemy
      const spriteIdentityChanged =
        !prev
        || prev.slug !== enemy.slug
        || Boolean(prev.isShiny) !== Boolean(enemy.isShiny)
      if (spriteIdentityChanged) {
        this.enemySpriteNonce += 1
      }
      this.enemy = enemy
      this.displayedHpPercent = 100
      this.isFighting = true

      this.startAutoAttack()

      if (enemy.isBoss && enemy.bossTimerSeconds) {
        this.bossTimeRemaining = enemy.bossTimerSeconds
        // Countdown is decremented in sync with damage ticks (overrideAutoAttack) so the
        // last hit is never skipped when the timer hits 0.
      }
    },

    autoAttackTick() {
      if (!this.enemy || this.enemy.currentHp <= 0 || this.teamDps <= 0) return
      this.enemy.currentHp = Math.max(0, this.enemy.currentHp - this.teamDps)
    },

    /**
     * Drift-correcting auto-attack loop.
     * Unlike setInterval, each tick self-schedules with a drift correction so the timer
     * stays accurate even when the JS event loop is temporarily blocked (e.g. heavy renders).
     */
    startAutoAttack() {
      const INTERVAL = 1000
      let expected = performance.now() + INTERVAL

      const tick = () => {
        if (!runCombatDamageTick()) {
          if (this.overrideAutoAttack) {
            this.overrideAutoAttack()
          } else {
            this.autoAttackTick()
          }
        }

        const now = performance.now()
        const drift = now - expected
        expected += INTERVAL
        // Schedule next tick compensating for accumulated drift (min 1ms)
        this.autoAttackHandle = setTimeout(tick, Math.max(1, INTERVAL - drift))
      }

      this.autoAttackHandle = setTimeout(tick, INTERVAL)
    },

    /**
     * Drift-correcting boss countdown timer.
     * Decrements bossTimeRemaining every ~1s with self-correcting scheduling.
     */
    startBossTimer() {
      if (this.bossTimerHandle) {
        clearTimeout(this.bossTimerHandle)
        this.bossTimerHandle = null
      }

      const INTERVAL = 1000
      let expected = performance.now() + INTERVAL

      const tick = () => {
        if (this.bossTimeRemaining !== null) {
          this.bossTimeRemaining--
        }

        if (this.bossTimeRemaining !== null && this.bossTimeRemaining > 0) {
          const now = performance.now()
          const drift = now - expected
          expected += INTERVAL
          this.bossTimerHandle = setTimeout(tick, Math.max(1, INTERVAL - drift))
        }
      }

      this.bossTimerHandle = setTimeout(tick, INTERVAL)
    },

    clearTimers() {
      if (this.autoAttackHandle) {
        clearTimeout(this.autoAttackHandle)
        this.autoAttackHandle = null
      }
      if (this.bossTimerHandle) {
        clearTimeout(this.bossTimerHandle)
        this.bossTimerHandle = null
      }
    },

    /** Enregistre le KO sans vider l’ennemi — permet d’enchaîner le spawn sans écran « Recherche ». */
    finalizeEncounterKill() {
      this.clearTimers()
      // recordKill() est déjà fait au crédit or (useCombatLoop) pour ancrer farm-sync.
      this.isFighting = false
      this.bossTimeRemaining = null
    },

    killEnemy() {
      this.finalizeEncounterKill()
      this.enemy = null
      this.displayedHpPercent = 0
    },

    bossFailed() {
      this.clearTimers()
      this.enemy = null
      this.isFighting = false
      this.bossTimeRemaining = null
      this.displayedHpPercent = 0
    },

    pauseForVisibility() {
      this.pausedByVisibility = true
      this.clearTimers()
    },

    /** Reprend la boucle client après onglet visible (force le redémarrage des timers). */
    resumeFromVisibility() {
      this.pausedByVisibility = false
      if (!this.enemy) return
      if (!this.isFighting) {
        this.isFighting = true
      }
      this.clearTimers()
      this.startAutoAttack()
    },

    /**
     * Relance les timers si un ennemi est présent mais la boucle s’est arrêtée
     * (clearTimers sans restart, reset Pinia, onglet masqué mal détecté, etc.).
     */
    ensureAttackLoop() {
      if (this.pausedByVisibility || this.isDying || !this.enemy) return
      if (this.enemy.currentHp <= 0) return
      if (!this.isFighting) {
        this.isFighting = true
      }
      if (!this.autoAttackHandle) {
        this.startAutoAttack()
      }
    },

    /** Called once per damage tick during boss fights (after damage is applied). */
    tickBossTimerAfterDamage() {
      if (this.bossTimeRemaining === null || this.bossTimeRemaining <= 0) return
      this.bossTimeRemaining--
    },

    /** Called each RAF frame to smoothly interpolate displayedHpPercent toward the real value. */
    lerpDisplayedHp() {
      const target = this.enemyHpPercent
      const current = this.displayedHpPercent
      if (Math.abs(target - current) < 0.1) {
        this.displayedHpPercent = target
      } else {
        // Lerp factor 0.12 → ~60ms to close 90% of the gap at 60fps
        this.displayedHpPercent = current + (target - current) * 0.12
      }
    },

    triggerHitFlash() {
      this.isHit = true
      setTimeout(() => { this.isHit = false }, 150)
    },

    triggerBigHitFlash() {
      this.bigHitFlash = true
      setTimeout(() => { this.bigHitFlash = false }, 300)
    },

    setDying(val: boolean) {
      this.isDying = val
    },

    upgradeTeamDps(amount: number) {
      this.teamDps += amount
    },

    reset() {
      this.clearTimers()
      this.$reset()
    },
  },
})
